<script context="module" lang="ts">
  import { addDays, isAfter, isBefore, format, endOfMonth, startOfMonth } from 'date-fns';

  // 日付フォーマット
  export function formatDate(targetDate: string) {
    return format(
      new Date(Number(targetDate.slice(0, 4)), Number(targetDate.slice(4, 6)) - 1, Number(targetDate.slice(6, 8))),
      'yyyy/MM/dd'
    );
  }

  // 7桁(hhmmsss),8桁(hhhmmss)の2パターンを考慮する
  export function formatTime(targetTime: string) {
    if (targetTime.length == 7) {
      return format(
        new Date().setHours(
          Number(targetTime.slice(0, 2)),
          Number(targetTime.slice(2, 4)),
          Number(targetTime.slice(4, 6)),
          // ミリ秒は3桁だがDBでは小数点第一位までしか保持しないため100倍する
          Number(targetTime.slice(6, 7)) * 100
        ),
        'HH:mm:ss.S'
      );
    } else {
      return format(
        new Date().setHours(
          Number(targetTime.slice(0, 3)),
          Number(targetTime.slice(3, 5)),
          Number(targetTime.slice(5, 7)),
          // ミリ秒は3桁だがDBでは小数点第一位までしか保持しないため100倍する
          Number(targetTime.slice(7, 8)) * 100
        ),
        'HHH:mm:ss.S'
      );
    }
  }

  /**
   * 文字列中の空白削除
   * @param target
   */
  export function replaceBlank(target: string): string {
    return target.replace(/\s+/g, '');
  }

  /**
   * 文字列の前後空白削除
   * @param target
   */
  export function trimBlank(target: string): string {
    return target.replace(/^[s|　]+|[s|　]+$/g, '');
  }

  // 全角カタカナチェック
  export function isZenkakuKana(target: string) {
    return !!target.match(/^[ァ-ヶ０-９ー　]*$/);
  }

  // 全角チェック
  export function isZenkaku(target: string) {
    return !!target.match(/^[^ -~｡-ﾟ]+$/);
  }

  // 半角数値チェック
  export function isHankakuNumber(target: string) {
    return !!target.match(/^[0-9]+$/);
  }

  // 全角カナを全角ひらがなに変換
  export function conversionKanaToHiragana(target: string) {
    return target.replace(/[ァ-ヶ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));
  }

  // 日付チェック(yyyyMMdd)
  export function isDate(targetDate: string) {
    if (!targetDate.match(/^\d{4}\d{2}\d{2}$/)) {
      return false;
    }
    let date = new Date(
      Number(targetDate.substr(0, 4)),
      Number(targetDate.substr(4, 2)) - 1,
      Number(targetDate.substr(6, 2))
    );
    // monthを比較して繰り上げられていなければ末日は問題なし
    return date.getMonth() + 1 == Number(targetDate.substr(4, 2));
  }

  /**
   * 対象日が当日+加算日より後ろかチェック
   * addDay = 0 : 明日以降, 1: 明後日以降...
   *
   * @param targetDate
   */
  export function isAfterDay(targetDate: string, addDay: number = 0) {
    return isAfter(
      new Date(Number(targetDate.substr(0, 4)), Number(targetDate.substr(4, 2)) - 1, Number(targetDate.substr(6, 2))),
      addDays(new Date(), addDay)
    );
  }

  /**
   * 対象日が N-2,N+2ヶ月の範囲かチェック
   * @param targetDate
   */
  export function isWithinTwoMonth(targetDate: string) {
    // 2ヶ月後
    var later = new Date();
    later.setDate(1);
    later.setMonth(later.getMonth() + 2);
    // 2ヶ月前
    var before = new Date();
    before.setDate(1);
    before.setMonth(before.getMonth() - 2);
    var target = changeYmNumber(targetDate);

    // 入力月+2ヶ月 AND 入力月-2ヶ月の範囲しか入力できない
    return Number(displayYm(before)) <= target && Number(displayYm(later)) >= target;
  }

  /**
   * Dateを文字列表示（yyyyMM）
   * @param dt
   */
  export function displayYm(dt: Date) {
    return format(dt, 'yyyyMM');
  }

  /**
   * 日付（文字列）を数値に変換
   */
  export function changeYmNumber(targetDate: string) {
    return Number(targetDate.replaceAll('-', '').slice(0, 6));
  }

  /**
   * 月末22時〜月初2時
   */
  export function isTheBeginningOfTheEndOfTheMonth() {
    // 現在日時
    const now = new Date();
    // 月末22時
    let endOfMonthTime = endOfMonth(now);
    endOfMonthTime.setHours(22);
    endOfMonthTime.setMinutes(0);
    endOfMonthTime.setSeconds(0);
    endOfMonthTime.setMilliseconds(0);

    // 月初2時
    let startOfMonthTime = startOfMonth(now);
    startOfMonthTime.setHours(2);

    // 月末22時 <= 現在日時 OR 月初2時 > 現在日時
    return isBefore(endOfMonthTime, now) || isAfter(startOfMonthTime, now);
  }

  /**
   * MNP予約番号発行可能
   * @param targetStatusCode
   */
  export function isAvailableMnpReserveNumberRequest(targetStatusCode: string) {
    return (
      !targetStatusCode ||
      targetStatusCode === 'ACCEPT_ERROR' ||
      targetStatusCode === 'ISSUE_ERROR' ||
      targetStatusCode === 'CANCEL' ||
      targetStatusCode === 'EXPIRED'
    );
  }

  /**
   * SIM一時停止・再開可能
   * @param targetStatusCode
   */
  export function isAvailableStopResumeRequest(targetStatusCode: string) {
    return ['ACCEPT', 'REQUEST'].includes(targetStatusCode);
  }
</script>
